import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { MapPin, Phone, Mail, Loader2, CheckCircle2, Landmark } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast({ title: "Message sent!", description: "We'll get back to you soon." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast({ title: "Failed", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero path="contact.hero" kicker="Contact Us" title="Get in touch" subtitle="Have a question? We'd love to hear from you. Send us a message and we'll respond as soon as possible." />
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          {/* Info cards */}
          <div className="space-y-5">
            <InfoCard icon={MapPin} title="Institute" lines={["Kiki Centre for Technology", "1/47, Sector 75A, Gurugram - 122012", "(IMT Manesar, Delhi-NCR)"]} link="https://maps.google.com/maps?ll=28.360862%2C76.916469&z=10&cid=17613659113676479500" />
            <InfoCard icon={Phone} title="Call Us" lines={["Admission Office: +91-8800288994", "Institute / Admin: 0124-4060694 / 5", "Corporate Cell: +91-9953848338"]} link="tel:+918800288994" />
            <InfoCard icon={Mail} title="Email Us" lines={["info@kikicentre.com"]} link="mailto:info@kikicentre.com" />
            <InfoCard icon={Landmark} title="Registered Office" lines={["Anjali Foundation", "159, Rabindra Sarani, 3rd Floor, Room No 3C", "Kolkata – 700007, India"]} />
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            {done ? (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-10 text-center">
                <CheckCircle2 className="h-14 w-14 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#0B2A6B] mb-2">Message Sent!</h3>
                <p className="text-slate-600 mb-6">We'll get back to you within 24 hours.</p>
                <Button onClick={() => setDone(false)} className="bg-[#0B2A6B] text-white">Send Another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-7 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name *"><Input required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" /></Field>
                  <Field label="Email *"><Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" /></Field>
                  <Field label="Phone"><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91..." /></Field>
                  <Field label="Subject"><Input value={form.subject} onChange={(e) => update("subject", e.target.value)} placeholder="How can we help?" /></Field>
                </div>
                <Field label="Message *"><Textarea required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us more..." /></Field>
                <Button type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold h-12 text-base">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Sending...</> : "Send Message"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* How to reach us */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-slate-50 border border-slate-100 p-8">
            <h2 className="text-2xl font-bold text-[#0B2A6B] mb-2">How to reach us</h2>
            <p className="text-slate-600 text-sm mb-6 max-w-3xl">
              Kiki Centre for Technology is situated at the well-regarded Industrial Model Township of Manesar (IMT Manesar),
              Gurgaon, in Delhi-NCR — just 5 minutes from the Maruti Suzuki and Honda Motorcycle & Scooter factories. It is well
              connected by National Highway, Railway, Metro and Bus.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                ["National Highway", "NH8 — take the exit for IMT Manesar. From Delhi, the exit is 5 km after the Manesar Toll."],
                ["Bus", "Haryana Roadways buses connect IMT Manesar to Gurgaon main city and the metro stations."],
                ["Metro Station", "IFFCO Chowk or Huda City Centre, Gurgaon — about 20 km (30 mins) away."],
                ["Railway (Local)", "Garhi Harsaru; Gurgaon station about 10 km (30 mins) away."],
                ["Railway (National)", "New Delhi Railway Station — about 45 km (2 hours) away."],
                ["Airport", "New Delhi T3 or T1 — about 30 km (45 mins) away."],
              ].map(([t, d]) => (
                <div key={t} className="bg-white rounded-xl p-5 border border-slate-100">
                  <h3 className="font-bold text-[#0B2A6B] text-sm mb-1">{t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-xl overflow-hidden border border-slate-200">
              <iframe
                title="Kiki Centre Map"
                src="https://www.google.com/maps?q=IMT+Manesar+Gurugram&output=embed"
                className="w-full h-72"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function InfoCard({ icon: Icon, title, lines, link }) {
  const content = (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-lg transition tilt-card">
      <div className="h-12 w-12 rounded-xl bg-yellow-400 flex items-center justify-center mb-3">
        <Icon className="h-6 w-6 text-[#0B2A6B]" />
      </div>
      <h3 className="font-bold text-[#0B2A6B] mb-1">{title}</h3>
      {lines.map((l, i) => (
        <p key={i} className="text-sm text-slate-600">{l}</p>
      ))}
    </div>
  );
  return link ? <a href={link}>{content}</a> : content;
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-700 mb-1.5 inline-block">{label}</Label>
      {children}
    </div>
  );
}
